// src/components/BillingReconPanel.tsx — 速報進捗ビュー(汎用フォーマット)(ベータ・2026-07-04 再設計)
//   会計リバエン速報化 B案。fact_billing_recon_daily(表示科×date×入外×bunrui の金額千円)を源に、
//   「○日=最新月の最新date」時点で 実績 / 予算(按分) / 前年実績(前年同月同○日累計・確報) を出す。
//   目的=経年でなく「現時点で予算達成ペースか / 前年を上回るペースか」(稲吉指示)。
//   ★モード切替(入院/入外合計・外来は UI 非表示で温存)+ 入院はドリルダウン(表示科別→診療No別→表示科×診療No別)。
//   単位=千円。診療No(bunrui)=診療識別。
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import type {
  BillingReconAsOf, BillingReconDetailRow, BillingReconDeptBudget, BillingReconHospitalRow,
  BillingCompareDaily, BillingCompareBudget,
} from "@shared/api";
import { billingBunruiName } from "@shared/api";
import { useSort, SortTh } from "../lib/useSort";
import { useFilters } from "../store";

const jpDate = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return `${y}年${m}月${d}日`;
};
// 千円 → 千円表示(3桁区切り・整数丸め)。
const kf = (v: number | null | undefined) =>
  v == null ? "" : Math.round(v).toLocaleString("ja-JP");
// 差分の符号色(プラス=青=目標超過ペース / マイナス=橙)。mcslide 慣例。
const diffCls = (v: number | null | undefined) =>
  v == null ? "text-slate-400" : v >= 0 ? "text-blue-700" : "text-orange-600";
const diffFmt = (v: number | null | undefined) =>
  v == null ? "" : (v >= 0 ? "+" : "") + Math.round(v).toLocaleString("ja-JP");
// 比(実績/基準)を % 表示。100%以上=青(達成/超過ペース)/未満=橙。
const ratioCls = (v: number | null | undefined) =>
  v == null ? "text-slate-400" : v >= 1 ? "text-blue-700" : "text-orange-600";
const ratioFmt = (v: number | null | undefined) =>
  v == null ? "" : (v * 100).toFixed(1) + "%";

// 表の 1 行に載せる派生指標付きの汎用行。
//   予算列は省略可(外来 dept など予算が無いモード=予算/予算差/予算比を隠す)。
interface PaceRow {
  key: string;
  名称: string;
  cd?: string | null;      // 行クリックの識別子(表示科cd / 診療No)。null=クリック不可(病院全体行など)
  実績: number | null;
  予算: number | null;
  前年実績: number | null;
}
const withDiff = (r: PaceRow) => ({
  ...r,
  予算差: r.実績 != null && r.予算 != null ? r.実績 - r.予算 : null,
  前年差: r.実績 != null && r.前年実績 != null ? r.実績 - r.前年実績 : null,
  予算比: r.実績 != null && r.予算 ? r.実績 / r.予算 : null,
  前年比: r.実績 != null && r.前年実績 ? r.実績 / r.前年実績 : null,
  構成比: null as number | null,   // その表の実績合計に対する比(0-1)。withPct() で後付け。
});
type Row = ReturnType<typeof withDiff>;

// 構成比列の分母 = その表の全表示行(pinnedRow 除く)の実績合計。
//   各行の 構成比 = 実績 ÷ Σ実績(0-1)。実績 null は分母に入れない/構成比 null。
const withPct = (rows: Row[]): Row[] => {
  const denom = rows.reduce((s, r) => s + (r.実績 ?? 0), 0);
  return rows.map((r) => ({
    ...r,
    構成比: r.実績 != null && denom ? r.実績 / denom : null,
  }));
};
// % 表示(構成比・0-1 → "12.3%")。合計行は常に 100.0%。
const pctFmt = (v: number | null | undefined) =>
  v == null ? "" : (v * 100).toFixed(1) + "%";

// 表示中の行(絞込適用後)の 実績/前年実績 を合算した「合計」pinnedRow を作る(予算なし表 ②③ 用)。
//   実績 or 前年実績 のどちらかに値がある行が 1 つでもあれば合計を出す(全 null なら undefined)。
const sumTotalRow = (rows: Row[], 名称: string): Row | undefined => {
  if (rows.length === 0) return undefined;
  const hasA = rows.some((r) => r.実績 != null);
  const hasP = rows.some((r) => r.前年実績 != null);
  if (!hasA && !hasP) return undefined;
  const 実績   = hasA ? rows.reduce((s, r) => s + (r.実績 ?? 0), 0) : null;
  const 前年実績 = hasP ? rows.reduce((s, r) => s + (r.前年実績 ?? 0), 0) : null;
  return withDiff({ key: `__合計_${名称}`, 名称, cd: null, 実績, 予算: null, 前年実績 });
};

// 汎用ペース表(予算列の有無 / 行クリック / 選択ハイライト に対応)。
//   スクロール無し=全行表示(maxHeight 撤去。稲吉指示)。
function PaceTable({
  rows, firstColLabel, showBudget, showPct, onRowClick, selectedCd, pinnedRow, note,
}: {
  rows: Row[];
  firstColLabel: string;              // 先頭列見出し(診療科 / 診療No 等)
  showBudget: boolean;                // 予算/予算差/予算比 列を出すか
  showPct?: boolean;                  // 構成比列(実績のすぐ右)を出すか
  onRowClick?: (cd: string) => void;  // 行クリック(cd 非 null 行のみ有効)
  selectedCd?: string | null;         // 選択中の cd(ハイライト)
  pinnedRow?: Row;                    // 先頭固定行(合計 / 入外合計 等・クリック不可)
  note?: string;
}) {
  const s = useSort(rows);
  const clickable = !!onRowClick;
  return (
    <div>
      <table className="text-[11px] border-collapse w-full">
        <thead className="bg-slate-50">
          <tr>
            <SortTh s={s} sortKey="名称" get={(r) => (r as Row).名称} className="px-2 py-1 text-left border-b bg-slate-50">{firstColLabel}</SortTh>
            <SortTh s={s} sortKey="実績" get={(r) => (r as Row).実績} className="px-2 py-1 text-right border-b whitespace-nowrap">実績</SortTh>
            {showPct && <SortTh s={s} sortKey="構成比" get={(r) => (r as Row).構成比} className="px-2 py-1 text-right border-b whitespace-nowrap">構成比</SortTh>}
            {showBudget && <SortTh s={s} sortKey="予算" get={(r) => (r as Row).予算} className="px-2 py-1 text-right border-b whitespace-nowrap">予算</SortTh>}
            {showBudget && <SortTh s={s} sortKey="予算差" get={(r) => (r as Row).予算差} className="px-2 py-1 text-right border-b whitespace-nowrap">予算差</SortTh>}
            {showBudget && <SortTh s={s} sortKey="予算比" get={(r) => (r as Row).予算比} className="px-2 py-1 text-right border-b whitespace-nowrap">予算比</SortTh>}
            <SortTh s={s} sortKey="前年実績" get={(r) => (r as Row).前年実績} className="px-2 py-1 text-right border-b whitespace-nowrap">前年実績</SortTh>
            <SortTh s={s} sortKey="前年差" get={(r) => (r as Row).前年差} className="px-2 py-1 text-right border-b whitespace-nowrap">前年差</SortTh>
            <SortTh s={s} sortKey="前年比" get={(r) => (r as Row).前年比} className="px-2 py-1 text-right border-b whitespace-nowrap">前年比</SortTh>
          </tr>
        </thead>
        <tbody>
          {pinnedRow && (
            <tr className="bg-slate-100 font-bold">
              <td className="px-2 py-1 border-b">{pinnedRow.名称}</td>
              <td className="px-2 py-1 text-right border-b tabular-nums">{kf(pinnedRow.実績)}</td>
              {showPct && <td className="px-2 py-1 text-right border-b tabular-nums">{pctFmt(pinnedRow.実績 != null ? 1 : null)}</td>}
              {showBudget && <td className="px-2 py-1 text-right border-b tabular-nums">{kf(pinnedRow.予算)}</td>}
              {showBudget && <td className={`px-2 py-1 text-right border-b tabular-nums ${diffCls(pinnedRow.予算差)}`}>{diffFmt(pinnedRow.予算差)}</td>}
              {showBudget && <td className={`px-2 py-1 text-right border-b tabular-nums ${ratioCls(pinnedRow.予算比)}`}>{ratioFmt(pinnedRow.予算比)}</td>}
              <td className="px-2 py-1 text-right border-b tabular-nums">{kf(pinnedRow.前年実績)}</td>
              <td className={`px-2 py-1 text-right border-b tabular-nums ${diffCls(pinnedRow.前年差)}`}>{diffFmt(pinnedRow.前年差)}</td>
              <td className={`px-2 py-1 text-right border-b tabular-nums ${ratioCls(pinnedRow.前年比)}`}>{ratioFmt(pinnedRow.前年比)}</td>
            </tr>
          )}
          {s.rows.map((d) => {
            const canClick = clickable && d.cd != null;
            const selected = selectedCd != null && d.cd === selectedCd;
            return (
              <tr key={d.key}
                onClick={() => canClick && onRowClick!(d.cd as string)}
                className={[
                  selected ? "bg-blue-100" : "hover:bg-blue-50",
                  canClick ? "cursor-pointer" : "",
                ].join(" ")}
                title={canClick ? (selected ? "クリックで選択解除" : "クリックで絞り込み") : undefined}
              >
                <td className="px-2 py-1 text-left border-b whitespace-nowrap">
                  {selected && <span className="text-blue-600 mr-0.5">▶</span>}{d.名称}
                </td>
                <td className="px-2 py-1 text-right border-b tabular-nums">{kf(d.実績)}</td>
                {showPct && <td className="px-2 py-1 text-right border-b tabular-nums text-slate-500">{pctFmt(d.構成比)}</td>}
                {showBudget && <td className="px-2 py-1 text-right border-b tabular-nums">{kf(d.予算)}</td>}
                {showBudget && <td className={`px-2 py-1 text-right border-b tabular-nums ${diffCls(d.予算差)}`}>{diffFmt(d.予算差)}</td>}
                {showBudget && <td className={`px-2 py-1 text-right border-b tabular-nums ${ratioCls(d.予算比)}`}>{ratioFmt(d.予算比)}</td>}
                <td className="px-2 py-1 text-right border-b tabular-nums">{kf(d.前年実績)}</td>
                <td className={`px-2 py-1 text-right border-b tabular-nums ${diffCls(d.前年差)}`}>{diffFmt(d.前年差)}</td>
                <td className={`px-2 py-1 text-right border-b tabular-nums ${ratioCls(d.前年比)}`}>{ratioFmt(d.前年比)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {note && <p className="text-[10px] text-slate-400 mt-1">{note}</p>}
    </div>
  );
}

// 集約ヘルパ: detail を任意キーで畳んで 実績/前年実績 を合算。
function aggregate<K extends string>(
  detail: BillingReconDetailRow[],
  keyOf: (r: BillingReconDetailRow) => K | null,
): Map<K, { 実績: number | null; 前年実績: number | null }> {
  const map = new Map<K, { 実績: number | null; 前年実績: number | null }>();
  for (const r of detail) {
    const k = keyOf(r);
    if (k == null) continue;
    const cur = map.get(k) ?? { 実績: null, 前年実績: null };
    if (r.実績 != null) cur.実績 = (cur.実績 ?? 0) + r.実績;
    if (r.前年実績 != null) cur.前年実績 = (cur.前年実績 ?? 0) + r.前年実績;
    map.set(k, cur);
  }
  return map;
}

// ════════════════════════════════════════════════════════════════════════
// 4層比較（日次/速報/確報/PL予算・2026-07-09 akira 指示）
//   日次 = 生データ(datijirphd リアルタイム・日次バッチで前々日まで取得。basis値も'日次'・旧称 無印)。
//   速報 = n-family 会計口座(未締め最新月・5日ごと程度の更新)。
//   確報 = d-family 会計口座(締め済み)。
//   予算 = PL 予算(月次)。日割り = 月予算 × データ最終日 ÷ 月日数。
//   ★basis ごとにデータ最終日が異なるため、達成率は各 basis 自身の最終日で按分した予算に対して出す。
// ════════════════════════════════════════════════════════════════════════
const BASIS_ORDER = ["日次", "速報", "確報"] as const;
const BASIS_COLOR: Record<string, string> = {
  日次: "#7c3aed",   // violet-600(生データ・旧称 無印)
  速報: "#d97706",   // amber-600
  確報: "#059669",   // emerald-600
};
const BASIS_BADGE: Record<string, string> = {
  日次: "bg-violet-100 text-violet-700",
  速報: "bg-amber-100 text-amber-700",
  確報: "bg-emerald-100 text-emerald-700",
};
const jpMonth = (iso: string) => {
  const [y, m] = iso.split("-").map(Number);
  return `${y}年${m}月`;
};

interface CompareRow {
  basis: string;
  lastDate: string;      // データ最終日(ISO)
  lastDay: number;       // 日(1..31)
  cum: number;           // 累計実績(千円)
  prorated: number | null;  // 日割り予算 = 月予算 × lastDay ÷ 月日数(千円)
  rate: number | null;      // 達成率 = 累計 ÷ 日割り予算
  monthRate: number | null; // 月予算消化率 = 累計 ÷ 月予算
}

function BillingCompareSection() {
  const { data, isFetching } = useQuery({
    queryKey: ["billing_compare"], queryFn: api.billingCompare, staleTime: 60_000,
  });
  const daily: BillingCompareDaily[] = data?.daily ?? [];
  const budget: BillingCompareBudget[] = data?.budget ?? [];
  const months: string[] = data?.months ?? [];

  const f = useFilters();
  const [selIo, setSelIo] = useState<"入院" | "外来">("入院");
  // 年月はサイドバー(FilterBar の 4層比較 年月)で選択(2026-07-09)。null=最新月。
  const month = f.billingCompareMonth ?? (months.length ? months[months.length - 1] : null);

  // 選択月×入外 の basis 別 日次累計系列と比較行。
  const { series, rows, bud } = useMemo(() => {
    if (!month) return { series: new Map<string, { day: number; cum: number }[]>(), rows: [] as CompareRow[], bud: null as BillingCompareBudget | null };
    const b = budget.find((x) => x.month === month && x.入外 === selIo) ?? null;
    const series = new Map<string, { day: number; cum: number }[]>();
    const rows: CompareRow[] = [];
    for (const basis of BASIS_ORDER) {
      const pts = daily
        .filter((r) => r.month === month && r.入外 === selIo && r.basis === basis)
        .sort((a, c) => a.day - c.day);
      if (pts.length === 0) continue;
      let cum = 0;
      const s = pts.map((p) => ({ day: p.day, cum: (cum += p.v) }));
      series.set(basis, s);
      const last = pts[pts.length - 1];
      const prorated = b ? (b.予算千円 * last.day) / b.daysInMonth : null;
      rows.push({
        basis, lastDate: last.date, lastDay: last.day, cum,
        prorated,
        rate: prorated ? cum / prorated : null,
        monthRate: b && b.予算千円 ? cum / b.予算千円 : null,
      });
    }
    return { series, rows, bud: b };
  }, [daily, budget, month, selIo]);

  if (!isFetching && months.length === 0) {
    return (
      <section className="card p-3 mb-3">
        <h2 className="text-sm font-bold mb-1">📊 4層比較（無印/速報/確報/予算）</h2>
        <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
          データ層 <code className="mx-1 px-1 bg-white rounded">fact_billing_raw_daily</code>（日次=生データ）が未生成です。
          日次バッチ(run_billing_recon_daily.sh)に組み込まれた
          <code className="mx-1 px-1 bg-white rounded">build_billing_raw_daily.R</code> の実行後に表示されます。
        </div>
      </section>
    );
  }

  // ── SVG 累計チャート ──
  const W = 720, H = 240, PAD_L = 56, PAD_R = 12, PAD_T = 10, PAD_B = 22;
  const daysInMonth = bud?.daysInMonth ?? 31;
  const yMax = Math.max(
    bud?.予算千円 ?? 0,
    ...Array.from(series.values()).map((s) => s[s.length - 1]?.cum ?? 0),
    1,
  ) * 1.05;
  const x = (day: number) => PAD_L + ((day - 0) / daysInMonth) * (W - PAD_L - PAD_R);
  const y = (v: number) => H - PAD_B - (v / yMax) * (H - PAD_T - PAD_B);
  const path = (s: { day: number; cum: number }[]) =>
    s.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.day).toFixed(1)},${y(p.cum).toFixed(1)}`).join(" ");
  // Y 軸目盛(0/25/50/75/100% of yMax・百万円表示)。
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * yMax);

  return (
    <section className="card p-3 mb-3">
      <h2 className="text-sm font-bold mb-1">
        📊 4層比較（日次/速報/確報/予算）
        {isFetching && <span className="text-slate-400 text-[11px] ml-2 font-normal">更新中…</span>}
      </h2>
      <p className="text-[11px] text-slate-500 mb-2">
        <span className={`px-1 py-0.5 rounded mr-1 ${BASIS_BADGE["日次"]}`}>日次</span>=生データ（リアルタイム・日次バッチで<b>前々日まで</b>・DPC包括は日割り概算）／
        <span className={`px-1 py-0.5 rounded mx-1 ${BASIS_BADGE["速報"]}`}>速報</span>=会計口座 n系（未締め最新月・約5日ごと更新）／
        <span className={`px-1 py-0.5 rounded mx-1 ${BASIS_BADGE["確報"]}`}>確報</span>=締め済み会計口座。
        金額＝込み（保険×10＋食事＋自費）<b>千円</b>。達成率＝累計実績 ÷ 日割り予算（月予算×データ最終日÷月日数）。
      </p>

      {/* 入外セレクタ(年月はサイドバー)。表示中の月をラベルで明示。 */}
      <div className="flex items-center gap-2 mb-2 text-[11px]">
        <span className="font-bold text-slate-700">{month ? jpMonth(month) : ""}</span>
        <div className="flex gap-1 ml-2">
          {(["入院", "外来"] as const).map((io) => (
            <button key={io}
              onClick={() => setSelIo(io)}
              className={`px-2 py-0.5 rounded border ${io === selIo ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"}`}>
              {io}
            </button>
          ))}
        </div>
      </div>

      {/* 比較表 */}
      <table className="text-[11px] border-collapse w-full mb-3">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-2 py-1 text-left border-b">基準</th>
            <th className="px-2 py-1 text-right border-b">データ最終日</th>
            <th className="px-2 py-1 text-right border-b">累計実績</th>
            <th className="px-2 py-1 text-right border-b">日割り予算(同日)</th>
            <th className="px-2 py-1 text-right border-b">達成率</th>
            <th className="px-2 py-1 text-right border-b">月予算</th>
            <th className="px-2 py-1 text-right border-b">月予算消化率</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.basis} className="hover:bg-blue-50">
              <td className="px-2 py-1 border-b">
                <span className={`px-1.5 py-0.5 rounded ${BASIS_BADGE[r.basis] ?? "bg-slate-100"}`}>{r.basis}</span>
              </td>
              <td className="px-2 py-1 text-right border-b tabular-nums">{jpDate(r.lastDate)}</td>
              <td className="px-2 py-1 text-right border-b tabular-nums">{kf(r.cum)}</td>
              <td className="px-2 py-1 text-right border-b tabular-nums">{kf(r.prorated)}</td>
              <td className={`px-2 py-1 text-right border-b tabular-nums ${ratioCls(r.rate)}`}>{ratioFmt(r.rate)}</td>
              <td className="px-2 py-1 text-right border-b tabular-nums">{kf(bud?.予算千円 ?? null)}</td>
              <td className="px-2 py-1 text-right border-b tabular-nums text-slate-600">{r.monthRate == null ? "" : (r.monthRate * 100).toFixed(1) + "%"}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={7} className="px-2 py-2 text-slate-400">選択月・入外にデータがありません。</td></tr>
          )}
        </tbody>
      </table>

      {/* 累計チャート */}
      <div className="overflow-x-auto">
        <svg width={W} height={H} className="block">
          {/* Y 軸グリッド + 目盛(百万円) */}
          {yTicks.map((t, i) => (
            <g key={i}>
              <line x1={PAD_L} y1={y(t)} x2={W - PAD_R} y2={y(t)} stroke="#e2e8f0" strokeWidth={1} />
              <text x={PAD_L - 4} y={y(t) + 3} textAnchor="end" fontSize={9} fill="#94a3b8">
                {Math.round(t / 1000).toLocaleString("ja-JP")}
              </text>
            </g>
          ))}
          <text x={4} y={PAD_T + 2} fontSize={9} fill="#94a3b8">百万円</text>
          {/* X 軸目盛(5日刻み) */}
          {Array.from({ length: Math.floor(daysInMonth / 5) + 1 }, (_, i) => i * 5).filter((d) => d >= 5 || d === 0).map((d) => (
            <text key={d} x={x(Math.max(d, 1))} y={H - PAD_B + 12} textAnchor="middle" fontSize={9} fill="#94a3b8">
              {d === 0 ? 1 : d}日
            </text>
          ))}
          {/* 予算日割り線(月初0 → 月末=月予算・破線) */}
          {bud && (
            <line x1={x(0)} y1={y(0)} x2={x(daysInMonth)} y2={y(bud.予算千円)}
              stroke="#64748b" strokeWidth={1.5} strokeDasharray="5,4" />
          )}
          {/* basis 別 累計線 */}
          {BASIS_ORDER.map((basis) => {
            const s = series.get(basis);
            if (!s) return null;
            return (
              <g key={basis}>
                <path d={`M${x(0).toFixed(1)},${y(0).toFixed(1)} ${path(s).replace(/^M/, "L")}`}
                  fill="none" stroke={BASIS_COLOR[basis]} strokeWidth={2} />
                <circle cx={x(s[s.length - 1].day)} cy={y(s[s.length - 1].cum)} r={3} fill={BASIS_COLOR[basis]} />
              </g>
            );
          })}
          {/* 凡例 */}
          {BASIS_ORDER.filter((b) => series.has(b)).map((basis, i) => (
            <g key={basis} transform={`translate(${PAD_L + 8 + i * 70}, ${PAD_T + 8})`}>
              <line x1={0} y1={0} x2={16} y2={0} stroke={BASIS_COLOR[basis]} strokeWidth={2} />
              <text x={20} y={3} fontSize={10} fill="#475569">{basis}</text>
            </g>
          ))}
          {bud && (
            <g transform={`translate(${PAD_L + 8 + series.size * 70}, ${PAD_T + 8})`}>
              <line x1={0} y1={0} x2={16} y2={0} stroke="#64748b" strokeWidth={1.5} strokeDasharray="5,4" />
              <text x={20} y={3} fontSize={10} fill="#475569">予算(日割り)</text>
            </g>
          )}
        </svg>
      </div>
      <p className="text-[10px] text-slate-400 mt-1">
        ※日次(=生データ・旧称 無印)の DPC 包括(入院料)は請求歴の claim 総点を包括期間×月の交差日数で均等日割りした概算(合計は claim と一致)。
        自費は RP 粒度(口座の自費総額と完全等価ではない)。未締め月の速報・日次は訂正進行中の値=確報で確定。
        日次と速報/確報の差の機序カタログ = 会計RP請求金額付与 doc/請求金額のRP分解_全体像.md。
      </p>
    </section>
  );
}

export function BillingReconPanel() {
  const f = useFilters();
  const mode = f.billingReconMode;
  const { data, isFetching } = useQuery({
    queryKey: ["billing_recon_pace"], queryFn: api.billingRecon, staleTime: 60_000,
  });
  const detail: BillingReconDetailRow[] = data?.detail ?? [];
  const deptBudget: BillingReconDeptBudget[] = data?.deptBudget ?? [];
  const hospital: BillingReconHospitalRow[] = data?.hospital ?? [];
  const asOf: BillingReconAsOf | null = data?.asOf ?? null;

  // 表示科cd → 表示科名 の解決(detail から。fold 済なので 1 対 1)。
  const deptName = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of detail) if (!m.has(r.表示科cd)) m.set(r.表示科cd, r.表示科名);
    return m;
  }, [detail]);
  // 表示科cd → 入院按分予算(千円)。
  const deptBudgetMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of deptBudget) if (b.入外 === "入院" && b.予算 != null) m.set(b.表示科cd, b.予算);
    return m;
  }, [deptBudget]);

  // ── 入外合計モード(旧 病院全体): 入院行 + 外来行 ──
  const hospitalRows: Row[] = useMemo(() =>
    hospital
      .slice()
      .sort((a, b) => (a.入外 === "入院" ? -1 : 1) - (b.入外 === "入院" ? -1 : 1))
      .map((h) => withDiff({ key: h.入外, 名称: h.入外, cd: null, 実績: h.実績, 予算: h.予算, 前年実績: h.前年実績 })),
    [hospital]);

  // ── 入院合計 行(hospital の 入外='入院' 行)。入院モード ① の pinnedRow(合計行)に使う。
  //   実績/予算/前年実績 は病院全体(全診療科合算)の入院分。予算差/予算比/前年差/前年比 は withDiff で派生。
  const nyuinTotalRow: Row | undefined = useMemo(() => {
    const h = hospital.find((r) => r.入外 === "入院");
    if (!h) return undefined;
    return withDiff({ key: "__入院合計", 名称: "入院合計", cd: null, 実績: h.実績, 予算: h.予算, 前年実績: h.前年実績 });
  }, [hospital]);

  // ── 外来モード: 外来 表示科別(予算なし)──
  const gairaiDeptRows: Row[] = useMemo(() => {
    const agg = aggregate(detail.filter((r) => r.入外 === "外来"), (r) => r.表示科cd);
    return Array.from(agg.entries())
      .map(([cd, v]) => withDiff({ key: cd, 名称: deptName.get(cd) ?? cd, cd, 実績: v.実績, 予算: null, 前年実績: v.前年実績 }))
      .sort((a, b) => (b.実績 ?? -Infinity) - (a.実績 ?? -Infinity));
  }, [detail, deptName]);

  // ── 入院モード ①表示科別 ──
  //   構成比 = 各科の実績 ÷ 表示科合計。合計行(pinnedRow)は 入院合計(nyuinTotalRow)。
  const nyuinDeptRows: Row[] = useMemo(() => {
    const agg = aggregate(detail.filter((r) => r.入外 === "入院"), (r) => r.表示科cd);
    const rows = Array.from(agg.entries())
      .map(([cd, v]) => withDiff({ key: cd, 名称: deptName.get(cd) ?? cd, cd, 実績: v.実績, 予算: deptBudgetMap.get(cd) ?? null, 前年実績: v.前年実績 }))
      .sort((a, b) => (b.実績 ?? -Infinity) - (a.実績 ?? -Infinity));
    return withPct(rows);
  }, [detail, deptName, deptBudgetMap]);

  // ── 入院モード ②診療No(bunrui)別: 選択科ありならその科、無ければ全科合算 ──
  const selDept = f.billingReconDept;
  const nyuinBunruiRows: Row[] = useMemo(() => {
    const src = detail.filter((r) => r.入外 === "入院" && (selDept == null || r.表示科cd === selDept));
    const agg = aggregate(src, (r) => r.bunrui);
    const rows = Array.from(agg.entries())
      .map(([no, v]) => withDiff({ key: no, 名称: `${no}:${billingBunruiName(no)}`, cd: no, 実績: v.実績, 予算: null, 前年実績: v.前年実績 }))
      .sort((a, b) => (b.実績 ?? -Infinity) - (a.実績 ?? -Infinity));
    return withPct(rows);
  }, [detail, selDept]);

  // ── 入院モード ③表示科×診療No別 ──
  //   絞込なし(全表示科×全診療No)は組合せが多く重いので、科 or 診療No が選択されている時のみ全件、
  //   どちらも未選択なら 実績上位 40 組合せ に丸める。
  const selBunrui = f.billingReconBunrui;
  const nyuinCrossRows: Row[] = useMemo(() => {
    const src = detail.filter((r) =>
      r.入外 === "入院" &&
      (selDept == null || r.表示科cd === selDept) &&
      (selBunrui == null || r.bunrui === selBunrui));
    const rows = src
      .filter((r) => r.実績 != null || r.前年実績 != null)
      .map((r) => withDiff({
        key: `${r.表示科cd}_${r.bunrui}`,
        名称: `${deptName.get(r.表示科cd) ?? r.表示科cd} / ${r.bunrui}:${billingBunruiName(r.bunrui)}`,
        cd: null, 実績: r.実績, 予算: null, 前年実績: r.前年実績,
      }))
      .sort((a, b) => (b.実績 ?? -Infinity) - (a.実績 ?? -Infinity));
    const unfiltered = selDept == null && selBunrui == null;
    return withPct(unfiltered ? rows.slice(0, 40) : rows);
  }, [detail, deptName, selDept, selBunrui]);

  const basisBadge = asOf?.basis === "速報"
    ? { txt: "速報", cls: "bg-amber-100 text-amber-700" }
    : { txt: "確報", cls: "bg-emerald-100 text-emerald-700" };

  const hasData = detail.length > 0 || hospital.length > 0;

  return (
    <div>
      <section className="card p-3 mb-3">
        <h2 className="text-sm font-bold mb-1">
          🏃 速報進捗ビュー（{mode}）
          {asOf && (
            <span className={`text-[10px] ml-2 px-1.5 py-0.5 rounded ${basisBadge.cls}`}>{basisBadge.txt}</span>
          )}
          {isFetching && <span className="text-slate-400 text-[11px] ml-2 font-normal">更新中…</span>}
        </h2>
        {asOf ? (
          <p className="text-[11px] text-slate-500 mb-2">
            <b>{asOf.day}日時点</b>（{jpDate(asOf.date)}・{basisBadge.txt}）の予算達成ペース／前年比。
            実績＝当月 1日〜{asOf.day}日 累計。予算＝月予算 × {asOf.day} ÷ {asOf.daysInMonth}（日数按分）。
            前年実績＝前年同月 1日〜{asOf.day}日 累計（確報）。単位＝<b>千円</b>。
            <span className="text-blue-700">＋青</span>＝上回るペース／<span className="text-orange-600">−橙</span>＝下回るペース。
          </p>
        ) : (
          <p className="text-[11px] text-slate-500 mb-2">データ層(fact_billing_recon_daily)を確認中…</p>
        )}

        {!hasData ? (
          <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
            データ層 <code className="mx-1 px-1 bg-white rounded">fact_billing_recon_daily</code> が未生成です。
            サイドローダ
            <code className="mx-1 px-1 bg-white rounded">aggregation/script/50_dashboard_build/build_billing_recon_daily.R</code>
            を実行し、Hono を再起動してください。
          </div>
        ) : mode === "入外合計" ? (
          <PaceTable
            rows={hospitalRows}
            firstColLabel="区分"
            showBudget
            note="※入外合計=入院行+外来行(fact を診療科問わず直接合算・予算=PL 入院/外来収益)。実績＝会計口座の速報 recon(保険＋食事＋自費 込み)。"
          />
        ) : mode === "外来" ? (
          <PaceTable
            rows={gairaiDeptRows}
            firstColLabel="診療科(外来)"
            showBudget={false}
            note="※外来は表示科別の 実績／前年実績のみ(外来の科別予算は無いため予算列なし)。会計口座の速報 recon(保険＋食事＋自費 込み)。"
          />
        ) : (
          // ── 入院モード: 3 表ドリル ──
          <div className="space-y-4">
            <div>
              <h3 className="text-xs font-semibold text-slate-600 mb-1">① 表示科別（行クリックで下の②③を絞り込み）</h3>
              <PaceTable
                rows={nyuinDeptRows}
                firstColLabel="診療科(入院)"
                showBudget
                showPct
                pinnedRow={nyuinTotalRow}
                onRowClick={(cd) => f.setBillingReconDept(f.billingReconDept === cd ? null : cd)}
                selectedCd={f.billingReconDept}
                note="※合計行=入院合計(病院全体の入院分・全診療科合算)。構成比=各科の実績÷表示科合計。各科の予算は fact_billing_budget_dept(表示科合算)。予算比＝PL 入院収益と整合。"
              />
            </div>
            <div>
              <h3 className="text-xs font-semibold text-slate-600 mb-1">
                ② 診療No別{selDept ? `（${deptName.get(selDept) ?? selDept}）` : "（全科合算）"}
                <span className="font-normal text-slate-400 ml-1">行クリックで③を絞り込み</span>
              </h3>
              <PaceTable
                rows={nyuinBunruiRows}
                firstColLabel="診療No"
                showBudget={false}
                showPct
                pinnedRow={sumTotalRow(nyuinBunruiRows, selDept ? `合計（${deptName.get(selDept) ?? selDept}）` : "合計（全科）")}
                onRowClick={(no) => f.setBillingReconBunrui(f.billingReconBunrui === no ? null : no)}
                selectedCd={f.billingReconBunrui}
                note="※合計行=今表示している診療Noの実績/前年実績の合計。構成比=各診療Noの実績÷合計。診療No(診療識別)=10初再診/20投薬/30注射/40処置/50手術・麻酔/60検査/70画像/80その他/90入院基本料/92特定入院料/97食事/99自費。予算は診療No粒度では無いため予算列なし。"
              />
            </div>
            <div>
              <h3 className="text-xs font-semibold text-slate-600 mb-1">
                ③ 表示科×診療No別
                {(selDept || selBunrui)
                  ? `（${selDept ? (deptName.get(selDept) ?? selDept) : "全科"} × ${selBunrui ? `${selBunrui}:${billingBunruiName(selBunrui)}` : "全診療No"}）`
                  : "（上位40組合せ・科 or 診療No を選ぶと全件）"}
              </h3>
              <PaceTable
                rows={nyuinCrossRows}
                firstColLabel="表示科 / 診療No"
                showBudget={false}
                showPct
                pinnedRow={sumTotalRow(nyuinCrossRows, "合計（表示中）")}
                note="※合計行=今表示している組合せの実績/前年実績の合計。構成比=各組合せの実績÷合計。表示科×診療No の 実績／前年実績。予算はこの粒度では無いため予算列なし。"
              />
            </div>
          </div>
        )}
      </section>

      <BillingCompareSection />

      <p className="text-[10px] text-slate-400 mt-2">
        ※○日 = fact_billing_recon_daily の最新月・最新date を自動採用（ハードコード月なし）。速報(basis='速報')が
        入れば当月に自動切替。B案(速報化設計.md §9)= 口座 rollup dt.Z を oracle 直読み・GUI月次CSV非依存。
        モード切替・絞り込みはサイドバーで操作。
      </p>
    </div>
  );
}
